/**
 * A single-series horizontal bar chart: 0–100 grey track, coloured fill.
 * Group names and the end value are HTML so the figure stays left-justified;
 * Vega only draws the track and bar.
 */

import { useMemo } from 'react';

import VegaChart, { type ChartSpec } from './VegaChart';
import Button from '@/components/ui/Button';

export type BarChartRow = {
  category: string;
  value: number;
  color?: string;
};

type BarChartProps = {
  rows: readonly BarChartRow[];
  ariaLabel: string;
  title?: string;
  emptyMessage?: string;
  height?: number;
  className?: string;
  color?: string;
  xMax?: number;
  formatValue?: (value: number) => string;
  action?: { label: string; onClick: () => void };
};

const DEFAULT_PLOT_HEIGHT = 120;
const BAR_BAND = 0.85;
const MAX_BAR_HEIGHT = 36;
const BAR_RADIUS = 4;
const DEFAULT_COLOR = '#0072B2';
const formatPercent = (value: number) => `${value}%`;

function barSpec(
  rows: readonly BarChartRow[],
  height: number,
  barHeight: number,
  xMax: number,
  defaultColor: string
): ChartSpec {
  const categories = rows.map((row) => row.category);
  const values = rows.map((row) => ({
    category: row.category,
    amount: row.value,
    fill: row.color ?? defaultColor
  }));
  const y = {
    field: 'category',
    type: 'nominal' as const,
    sort: categories,
    scale: { domain: categories, paddingInner: 0.18, paddingOuter: 0.06 },
    axis: null
  };
  const xScale = { domain: [0, xMax], nice: false, zero: true };

  return {
    width: 'container',
    height,
    autosize: { type: 'fit', contains: 'padding' },
    padding: 0,
    config: { padding: 0, view: { clip: false, stroke: null } },
    data: { values },
    layer: [
      {
        mark: {
          type: 'bar',
          height: barHeight,
          cornerRadius: BAR_RADIUS,
          color: '#E2E8F0'
        },
        encoding: {
          y,
          x: { type: 'quantitative', scale: xScale, datum: 0, axis: null },
          x2: { datum: xMax }
        }
      },
      {
        mark: { type: 'bar', height: barHeight, cornerRadius: BAR_RADIUS },
        encoding: {
          y,
          x: {
            field: 'amount',
            type: 'quantitative',
            scale: xScale,
            axis: null
          },
          color: {
            field: 'category',
            type: 'nominal',
            scale: { domain: categories, range: values.map((row) => row.fill) },
            legend: null
          },
          tooltip: [
            { field: 'category', title: 'Group' },
            { field: 'amount', title: 'Value' }
          ]
        }
      }
    ]
  };
}

export default function BarChart({
  rows,
  ariaLabel,
  title,
  emptyMessage = 'Nothing to show',
  height = DEFAULT_PLOT_HEIGHT,
  className,
  color = DEFAULT_COLOR,
  xMax = 100,
  formatValue = formatPercent,
  action
}: BarChartProps) {
  const barHeight = Math.min(MAX_BAR_HEIGHT, (height / Math.max(rows.length, 1)) * BAR_BAND);
  const valueFontSize = Math.max(16, Math.round(barHeight * 0.7));
  const spec = useMemo(() => barSpec(rows, height, barHeight, xMax, color), [rows, height, barHeight, xMax, color]);

  return (
    <div
      className={`flex h-full flex-col justify-center gap-2 rounded-xl border-2 border-border-subtle px-3 py-3 ${className ?? ''}`}
    >
      {(title || action) && (
        <div className="flex items-center justify-between gap-2">
          {title ? <p className="text-sm font-semibold text-content-default">{title}</p> : <span />}
          {action ? (
            <Button variant="outline" size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          ) : null}
        </div>
      )}
      {rows.length > 0 ? (
        <div className="flex w-full min-w-0 items-stretch gap-2" style={{ height }} aria-label={ariaLabel}>
          <ul className="flex w-28 shrink-0 flex-col">
            {rows.map((row) => (
              <li key={row.category} className="flex min-w-0 flex-1 items-center">
                <span className="truncate text-xs text-content-default" title={row.category}>
                  {row.category}
                </span>
              </li>
            ))}
          </ul>
          <div className="min-w-0 flex-1 overflow-hidden [&_.vega-embed]:block [&_.vega-embed]:w-full [&_svg]:block">
            <VegaChart spec={spec} ariaLabel={ariaLabel} />
          </div>
          <ul className="flex shrink-0 flex-col">
            {rows.map((row) => (
              <li key={row.category} className="flex flex-1 items-center justify-end">
                <span className="tabular-nums font-semibold text-content-default" style={{ fontSize: valueFontSize }}>
                  {formatValue(row.value)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex items-center justify-center text-center text-xs text-content-subtle" style={{ height }}>
          {emptyMessage}
        </div>
      )}
    </div>
  );
}
